// Owner-locked draw. Pure rules for cards that "draw a spell which only
// <source> may cast" (Algor/Char/Dank/Torrid Omphalos, Morgana le Fay,
// Archangel Gabriel). No React, no network.
//
// ── Sorcery rule ──
// Some cards draw a spell that may only be cast by a specific permanent
// (the drawing source itself, or a chosen allied minion). The drawn
// copy carries a lock to that permanent's instance id. The lock is a
// property of that one drawn copy in hand: cast it and it's gone; if it
// ends up in the cemetery and is replayed, no lock applies.
//
// The lock is stored as `_castableOnlyBy: <cardInstanceId>`. A plain
// serializable string, so it rides natively on the cardInstance when
// the card is later played face-up (placeCard ships the whole instance)
// and survives pile/state reconciliation.

import { hasKeyword } from './keywordQuery';
import { countsAsUnit } from './typeline';
import { isDisabled } from '../automation/scope';

/**
 * Tag a freshly drawn card so only `ownerId` may cast it. Mutates in
 * place. A falsy ownerId is a no-op (an ordinary, unlocked draw).
 */
export function tagCastLock(card, ownerId) {
  if (!card || !ownerId) return;
  card._castableOnlyBy = ownerId;
}

/**
 * Resolve the owner instance id for a `draw` op's `args.owner`.
 *   'self'        → the source card's own instance id.
 *   'chosen_ally' → the pre-picked ally id passed in via `chosenAllyId`.
 * Returns null when no lock applies (plain draw) or the chosen ally is
 * missing.
 */
export function resolveOwnerId(owner, sourceCard, chosenAllyId = null) {
  if (owner === 'self') return sourceCard?.id || null;
  if (owner === 'chosen_ally') return chosenAllyId || null;
  return null;
}

/**
 * Can the owner permanent still cast its held spells? Per the Morgana
 * FAQ a silenced / disabled / transformed owner that LOST the casting
 * ability can no longer cast. The hand still exists (the controller may
 * view it), but no cast is legal.
 *
 *   • Disabled owner: no abilities at all → cannot cast.
 *   • Minion / avatar (Morgana, Gabriel's chosen ally): casts VIA the
 *     Spellcaster keyword, so it must currently HAVE that keyword.
 *     Silence / transform-into-a-non-spellcaster revokes it. `hasKeyword`
 *     already returns false for a silenced card.
 *   • Artifact / site (the four Omphalos): casts its drawn spell
 *     inherently and is NOT a Spellcaster. It stays able to cast.
 */
export function ownerCanCastLocked(ownerCard) {
  if (!ownerCard) return false;
  if (isDisabled(ownerCard)) return false;
  if (countsAsUnit(ownerCard)) return hasKeyword(ownerCard, 'spellcaster');
  return true;
}

/**
 * Cast-eligibility predicate. A card with `_castableOnlyBy` set may be
 * cast only by the owner permanent it names, only while that owner is
 * still on the board, AND only while that owner can still cast spells.
 *
 * @param {object} card        the card being played from hand
 * @param {(id:string)=>boolean} ownerInPlay  is the owner permanent live?
 * @param {string|null} castingPermanentId  the permanent doing the cast,
 *   when the cast pipeline supplies one (dormant today. Pass null).
 * @param {(id:string)=>boolean} ownerCanCast  may the owner still cast?
 *   Defaults to always-true so existing callers keep the presence-only
 *   behavior; pass a resolver to enforce the silence / disable gate.
 * @returns {{ ok: true } | { ok: false, reason: 'owner_locked', message: string }}
 */
export function checkCastLock(card, ownerInPlay, castingPermanentId = null, ownerCanCast = () => true) {
  const ownerId = card?._castableOnlyBy;
  if (!ownerId) return { ok: true };
  if (!ownerInPlay(ownerId)) {
    return {
      ok: false,
      reason: 'owner_locked',
      message: `${card.name || 'This spell'} can only be cast by the card that drew it, which has left play.`,
    };
  }
  if (!ownerCanCast(ownerId)) {
    return {
      ok: false,
      reason: 'owner_locked',
      message: `${card.name || 'This spell'} can only be cast by its owner, which can no longer cast spells.`,
    };
  }
  if (castingPermanentId && castingPermanentId !== ownerId) {
    return {
      ok: false,
      reason: 'owner_locked',
      message: `${card.name || 'This spell'} can only be cast by the card that drew it.`,
    };
  }
  return { ok: true };
}

/** Clear the lock when the card leaves the realm / re-enters a pile. */
export function clearCastLock(card) {
  if (card) delete card._castableOnlyBy;
}
